import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { Card, CardContent } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import {
  Shield,
  KeyRound,
  Lock,
  Fingerprint,
  QrCode,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { resolveTenantBranding } from '@/utils/tenantBranding';
import { isBankAppHost } from '@/utils/domainDetection';
import BrandLogo from '@/components/shared/BrandLogo';

const SecurityTokenLandingSection: React.FC = () => {
  const navigate = useNavigate();
  const tenant = resolveTenantBranding();
  const isBank = isBankAppHost();

  const productName = isBank ? `${tenant.displayName} Tokenized Pay` : 'Vaiu Tokenized Pay';

  const pillars = [
    {
      icon: Shield,
      title: 'Bank-grade security',
      description:
        'Payments move as unique tokens—not shared card numbers. Each token is scoped, traceable, and revocable.',
    },
    {
      icon: KeyRound,
      title: 'Token technology',
      description:
        'Create cash tokens, request-money tokens, and ID tokens with optional conditions, expiry, and multi-use rules.',
    },
    {
      icon: Lock,
      title: 'Privacy by design',
      description:
        'Recipients redeem without exposing your full payment credentials. You stay in control of what each token can do.',
    },
  ];

  const highlights = [
    'Encrypted redemption and audit-friendly ledger events',
    'Conditional acceptance (time, recipient, location)',
    'QR and link sharing—no app install required for guests',
    'Works across web, mobile, and partner bank experiences',
  ];

  return (
    <div className="pb-12">
      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <div className="max-w-3xl mx-auto text-center space-y-5">
          <div className="flex justify-center">
            <BrandLogo size="md" variant="default" className="max-h-12" />
          </div>
          <Badge variant="secondary" className="text-xs font-medium tracking-wide uppercase">
            Security-first payments
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">
            {productName}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {isBank
              ? `Send and receive money with ${tenant.displayName}'s tokenized payment network—built for security, not storefronts.`
              : 'The simplest way to pay and get paid with unique money tokens—secure, inclusive, and built for real-world trust.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button size="lg" onClick={() => navigate('/signup')} className="gap-2">
              Create account
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/login')}>
              Sign in
            </Button>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-5xl mx-auto grid gap-4 sm:grid-cols-3">
          {pillars.map(({ icon: Icon, title, description }) => (
            <Card key={title} className="border-gray-200/80 shadow-sm">
              <CardContent className="pt-6 space-y-3">
                <div className="inline-flex p-2.5 rounded-lg bg-slate-100">
                  <Icon className="w-5 h-5 text-slate-800" />
                </div>
                <h2 className="font-semibold text-gray-900">{title}</h2>
                <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Token flow + highlights */}
      <section className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-slate-200 bg-white/80">
            <CardContent className="pt-8 pb-8 space-y-6">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                  <QrCode className="w-4 h-4" />
                  <Fingerprint className="w-4 h-4" />
                  <span>How tokenized pay works</span>
                </div>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  You mint a token for an amount and purpose. The recipient redeems it through a
                  verified flow—no copying card numbers into chat or email.
                </p>
              </div>

              <ol className="grid gap-3 sm:grid-cols-3 text-sm text-gray-700">
                <li className="rounded-lg border bg-slate-50 p-4">
                  <span className="font-semibold text-gray-900">1. Create</span>
                  <p className="mt-1 text-gray-600">Set amount, currency, and optional rules.</p>
                </li>
                <li className="rounded-lg border bg-slate-50 p-4">
                  <span className="font-semibold text-gray-900">2. Share</span>
                  <p className="mt-1 text-gray-600">Send a code, link, or QR—only what you choose.</p>
                </li>
                <li className="rounded-lg border bg-slate-50 p-4">
                  <span className="font-semibold text-gray-900">3. Redeem</span>
                  <p className="mt-1 text-gray-600">Secure settlement with full audit trail.</p>
                </li>
              </ol>

              <ul className="grid gap-2 sm:grid-cols-2">
                {highlights.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default SecurityTokenLandingSection;
